"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.APIPluginManifestWrapper = exports.RuntimeType = void 0;
const generated_types_1 = require("../generated-types");
const BaseManifest_1 = require("./BaseManifest");
/**
 * Runtime type enum for plugin runtimes.
 * These values match the auto-generated RuntimeType from the schema.
 * @see RuntimeTypeValue for the type definition
 */
exports.RuntimeType = {
    OpenApi: "OpenApi",
    LocalPlugin: "LocalPlugin",
    RemoteMCPServer: "RemoteMCPServer",
};
/**
 * OOP wrapper for API Plugin Manifest.
 *
 * Provides a fluent API for manipulating API plugin manifests with
 * type safety, state tracking, and convenient operations.
 *
 * @example
 * ```typescript
 * // Read existing manifest
 * const plugin = await APIPluginManifestWrapper.read("plugin.json");
 *
 * // Modify with fluent API
 * plugin
 *   .addFunction("getUser", "Get user information")
 *   .addOpenApiRuntime("openapi.yaml", "Bearer");
 *
 * // Save changes
 * await plugin.save();
 * ```
 */
class APIPluginManifestWrapper extends BaseManifest_1.BaseManifest {
    constructor(data, filePath) {
        super(data, filePath);
    }
    // ============= Static Factory Methods =============
    /**
     * Reads a plugin manifest from a file.
     * @param filePath - Path to the manifest JSON file.
     * @returns A new APIPluginManifestWrapper instance.
     */
    static async read(filePath) {
        const data = await generated_types_1.AppManifestUtils.readApiPluginManifest(filePath);
        return new APIPluginManifestWrapper(data, filePath);
    }
    /**
     * Reads a plugin manifest from a file synchronously.
     * @param filePath - Path to the manifest JSON file.
     * @returns A new APIPluginManifestWrapper instance.
     */
    static readSync(filePath) {
        const json = BaseManifest_1.BaseManifest.readJsonFileSync(filePath);
        const data = generated_types_1.ApiPluginManifestConverter.jsonToManifest(JSON.stringify(json));
        return new APIPluginManifestWrapper(data, filePath);
    }
    /**
     * Creates an APIPluginManifestWrapper from a JSON string.
     * @param json - JSON string representing the manifest.
     * @returns A new APIPluginManifestWrapper instance.
     */
    static fromJSON(json) {
        const data = generated_types_1.ApiPluginManifestConverter.jsonToManifest(json);
        return new APIPluginManifestWrapper(data);
    }
    /**
     * Creates a new plugin manifest with required fields.
     * @param init - Initial manifest data with required fields.
     * @returns A new APIPluginManifestWrapper instance.
     */
    static create(init) {
        const data = {
            schema_version: init.schemaVersion,
            name_for_human: init.nameForHuman,
            description_for_human: init.descriptionForHuman,
            namespace: init.namespace ?? init.nameForHuman.replace(/\s+/g, "_"),
        };
        return new APIPluginManifestWrapper(data);
    }
    // ============= Getters =============
    /**
     * Returns the schema version of the manifest.
     */
    get schemaVersion() {
        return this._data.schema_version;
    }
    /**
     * Returns the human-readable name.
     */
    get nameForHuman() {
        return this._data.name_for_human;
    }
    /**
     * Returns the human-readable description.
     */
    get descriptionForHuman() {
        return this._data.description_for_human;
    }
    /**
     * Returns the namespace (if available in the version).
     */
    get namespace() {
        return this._data.namespace;
    }
    /**
     * Returns a readonly array of functions.
     */
    get functions() {
        return this._data.functions ?? [];
    }
    /**
     * Returns a readonly array of runtimes.
     */
    get runtimes() {
        return this._data.runtimes ?? [];
    }
    // ============= Setters (Fluent API) =============
    /**
     * Sets the human-readable name.
     */
    setNameForHuman(name) {
        this._data.name_for_human = name;
        this.markDirty();
        return this;
    }
    /**
     * Sets the human-readable description.
     */
    setDescriptionForHuman(description) {
        this._data.description_for_human = description;
        this.markDirty();
        return this;
    }
    /**
     * Sets the model description.
     */
    setDescriptionForModel(description) {
        this._data.description_for_model = description;
        this.markDirty();
        return this;
    }
    /**
     * Sets the namespace (for latest version).
     */
    setNamespace(namespace) {
        this._data.namespace = namespace;
        this.markDirty();
        return this;
    }
    // ============= Function Operations =============
    /**
     * Adds a function to the manifest.
     * @param name - The function name (must match operationId in OpenAPI).
     * @param description - Description for the model.
     */
    addFunction(name, description) {
        if (!this._data.functions) {
            this._data.functions = [];
        }
        const existing = this._data.functions.find((f) => f.name === name);
        if (!existing) {
            this._data.functions.push({
                name,
                description,
            });
            this.markDirty();
        }
        return this;
    }
    /**
     * Removes a function by name.
     * @param name - The name of the function to remove.
     */
    removeFunction(name) {
        if (this._data.functions) {
            this._data.functions = this._data.functions.filter((f) => f.name !== name);
            this.markDirty();
        }
        return this;
    }
    /**
     * Checks if a function exists by name.
     */
    hasFunction(name) {
        return this.functions.some((f) => f.name === name);
    }
    /**
     * Gets a function by name.
     */
    getFunction(name) {
        return this.functions.find((f) => f.name === name);
    }
    // ============= Runtime Operations =============
    /**
     * Adds a runtime configuration.
     * @param runtime - The runtime configuration object.
     */
    addRuntime(runtime) {
        if (!this._data.runtimes) {
            this._data.runtimes = [];
        }
        this._data.runtimes.push(runtime);
        this.markDirty();
        return this;
    }
    /**
     * Adds an OpenAPI runtime.
     * @param specUrl - URL or relative path to the OpenAPI spec.
     * @param authType - Authentication type.
     * @param runForFunctions - Optional array of function names or "*" for all.
     */
    addOpenApiRuntime(specUrl, authType = "None", runForFunctions) {
        return this.addRuntime({
            type: exports.RuntimeType.OpenApi,
            auth: { type: authType },
            spec: { url: specUrl },
            run_for_functions: runForFunctions ?? ["*"],
        });
    }
    /**
     * Adds a LocalPlugin runtime.
     * @param localEndpoint - The local endpoint identifier.
     * @param runForFunctions - Optional array of function names.
     */
    addLocalPluginRuntime(localEndpoint, runForFunctions) {
        return this.addRuntime({
            type: exports.RuntimeType.LocalPlugin,
            auth: { type: "None" },
            spec: { local_endpoint: localEndpoint },
            run_for_functions: runForFunctions ?? ["*"],
        });
    }
    /**
     * Removes a runtime by spec URL.
     * @param specUrl - The spec URL of the runtime to remove.
     */
    removeRuntimeBySpecUrl(specUrl) {
        if (this._data.runtimes) {
            const runtimes = this._data.runtimes;
            this._data.runtimes = runtimes.filter((rt) => !(rt.spec?.url === specUrl));
            this.markDirty();
        }
        return this;
    }
    // ============= Query Operations =============
    /**
     * Returns all API spec paths/URLs from runtimes.
     */
    getApiSpecPaths() {
        return this.runtimes
            .filter((rt) => rt.type === exports.RuntimeType.OpenApi)
            .map((rt) => rt.spec?.url)
            .filter((url) => typeof url === "string");
    }
    // ============= Validation =============
    /**
     * Validates the manifest against its JSON schema.
     * @returns Array of validation error messages, empty if valid.
     */
    async validate() {
        return generated_types_1.AppManifestUtils.validateAgainstSchema(this._data);
    }
    // ============= Serialization =============
    /**
     * Converts the manifest to a formatted JSON string.
     */
    toJSON() {
        return generated_types_1.ApiPluginManifestConverter.manifestToJson(this._data);
    }
    /**
     * Creates a deep clone of this manifest.
     */
    clone() {
        const clonedData = JSON.parse(this.toJSON());
        return new APIPluginManifestWrapper(clonedData);
    }
    /**
     * Creates a deep clone with partial modifications applied.
     * Useful for creating a modified copy without mutating the original.
     * @param changes - Partial manifest data to merge into the clone. Allows additional properties for extensibility.
     * @returns A new APIPluginManifestWrapper with the changes applied.
     */
    cloneWith(changes) {
        const clonedData = JSON.parse(this.toJSON());
        Object.assign(clonedData, changes);
        const wrapper = new APIPluginManifestWrapper(clonedData);
        wrapper.markDirty();
        return wrapper;
    }
    /**
     * Returns a mutable reference to the manifest data for direct modification.
     * Use with caution - prefer using the fluent API methods when possible.
     * Changes made through this reference will be tracked as dirty.
     */
    get mutableData() {
        this.markDirty();
        return this._data;
    }
}
exports.APIPluginManifestWrapper = APIPluginManifestWrapper;
//# sourceMappingURL=APIPluginManifestWrapper.js.map